import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { createInquirySchema, type CreateInquiryInput } from '@tbbt/shared';
import { useSubmitInquiry } from '@/lib/api/queries';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/cn';
import { useSite } from '@/app/site-context';
import { countryByName, DEFAULT_COUNTRY, type Country } from '@/lib/countries';
import { Alert, Button, Field, Input, PhoneInput, Textarea } from '@/components/ui';

interface InquiryFormProps {
  /** Recorded against the enquiry, so the homepage and /contact are distinguishable. */
  sourcePath: string;
  /**
   * Namespaces the control ids.
   *
   * Required rather than defaulted: two copies of this form on one document would
   * otherwise share ids, and a `<label for>` would focus the wrong input.
   */
  idPrefix: string;
  labelVariant?: 'default' | 'caps';
  submitLabel?: string;
  /**
   * An action rendered beside the submit button — an "Email us" link, typically.
   *
   * Passed in rather than built here so the form stays a form: it knows how to validate
   * and post an enquiry, not what the alternatives to sending one are.
   */
  secondaryAction?: ReactNode;
  className?: string;
  submitClassName?: string;
}

/**
 * The public enquiry form.
 *
 * Four fields: name, email, an optional phone number, and the enquiry. Nothing else —
 * every additional box is a reason not to send the message, and a company name is
 * something you can read off the email domain or simply ask for in the reply.
 *
 * Shared by /contact and the homepage's closing block so there is exactly one place
 * where the payload, the honeypot and the server-error mapping are defined. Validated
 * with the *same* Zod schema the API validates against, imported from `@tbbt/shared`:
 * a rule cannot be tightened on the server and forgotten here, and the user gets the
 * identical message either way.
 */
export function InquiryForm({
  sourcePath,
  idPrefix,
  labelVariant = 'default',
  submitLabel = 'Send enquiry',
  secondaryAction,
  className,
  submitClassName,
}: InquiryFormProps): ReactNode {
  const submit = useSubmitInquiry();
  const { settings } = useSite();
  const [submitted, setSubmitted] = useState(false);

  /**
   * Which country the dialling code starts on.
   *
   * Taken from the primary office rather than hard-coded, so it follows the CMS. Only an
   * exact name match counts — guessing would silently prefill the wrong country code,
   * and a wrong number is worse than one the visitor had to select.
   */
  const initialCountry = useMemo(() => {
    const offices = settings.contact.offices;
    const primary = offices.find((office) => office.isPrimary) ?? offices[0];
    return (primary?.country ? countryByName(primary.country) : undefined) ?? DEFAULT_COUNTRY;
  }, [settings.contact.offices]);

  const [country, setCountry] = useState<Country>(initialCountry);

  /**
   * When the form was rendered.
   *
   * Sent as `elapsedMs` so the server can see how long the submission took. A `ref`
   * rather than state because it must not trigger a re-render, and it is set once on
   * mount rather than at module load so a long-lived tab does not report an
   * artificially large elapsed time.
   */
  const renderedAt = useRef<number | null>(null);

  useEffect(() => {
    // Set in an effect rather than as the ref's initial value: `Date.now()` during render
    // is an impure call, and under StrictMode's double render it would be evaluated twice.
    renderedAt.current = Date.now();
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setError,
  } = useForm<z.input<typeof createInquirySchema>, unknown, CreateInquiryInput>({
    // See the note in AuthPages: the schema's input and output types differ because of
    // `.default()`, so both must be supplied.
    resolver: zodResolver(createInquirySchema),
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      message: '',
      website: '',
    },
  });

  const id = (name: string): string => `${idPrefix}-${name}`;

  const onSubmit = handleSubmit(async (values) => {
    try {
      const national = values.phone?.trim() ?? '';

      await submit.mutateAsync({
        ...values,
        /*
          The dialling code is only attached when there is a number to attach it to.
          Sending "+971" on its own would store a phone value that cannot be called and
          would show up in the admin as though the visitor had left one.
        */
        phone: national === '' ? undefined : `+${country.dial} ${national}`,
        // Null until the mount effect runs, in which case the timing signal is simply
        // omitted rather than sent as a misleading zero.
        elapsedMs: renderedAt.current === null ? undefined : Date.now() - renderedAt.current,
        sourcePath,
      });
      setSubmitted(true);
      reset();
    } catch (error) {
      if (error instanceof ApiError) {
        // Map server field errors back onto the form, so a rule the client schema
        // could not check (a blocked domain, say) still highlights the right input.
        for (const [path, messages] of Object.entries(error.fieldErrors)) {
          const message = messages[0];
          if (message) {
            setError(path as keyof z.input<typeof createInquirySchema>, {
              type: 'server',
              message,
            });
          }
        }
      }
    }
  });

  if (submitted) {
    return (
      <div className={cn('flex flex-col items-start gap-4', className)}>
        <Alert tone="success" title="Thank you — we have your enquiry">
          Somebody will come back to you within one business day.
        </Alert>
        <Button variant="secondary" onClick={() => setSubmitted(false)}>
          Send another enquiry
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(event) => void onSubmit(event)}
      // `noValidate` disables the browser's own bubbles so our validation is the only
      // one the user sees — otherwise they get two different messages for the same
      // field, in two different styles.
      noValidate
      className={cn('flex flex-col gap-4', className)}
    >
      {submit.isError && submit.error instanceof ApiError ? (
        <Alert tone="danger" title="Could not send your enquiry">
          {submit.error.message}
        </Alert>
      ) : null}

      <Field
        label="Full name"
        htmlFor={id('fullName')}
        labelVariant={labelVariant}
        required
        error={errors.fullName?.message}
      >
        <Input
          id={id('fullName')}
          // One field, so `name` rather than `given-name` — a browser filling this from
          // a saved profile puts the whole name in, which is what we asked for.
          autoComplete="name"
          placeholder="Jane Doe"
          aria-invalid={Boolean(errors.fullName)}
          aria-describedby={errors.fullName ? `${id('fullName')}-error` : undefined}
          {...register('fullName')}
        />
      </Field>

      <Field
        label="Work email"
        htmlFor={id('email')}
        labelVariant={labelVariant}
        required
        error={errors.email?.message}
      >
        <Input
          id={id('email')}
          type="email"
          autoComplete="email"
          inputMode="email"
          placeholder="jane@company.com"
          aria-invalid={Boolean(errors.email)}
          aria-describedby={errors.email ? `${id('email')}-error` : undefined}
          {...register('email')}
        />
      </Field>

      {/*
        Its own row, not sharing one with the email box.

        The control is a country trigger and a number in a single field, and at half
        width the trigger left the national number about 100px — enough to clip its own
        placeholder. Splitting a composite control across a two-up row only works while
        the row is wide.

        "Optional" stays in the label rather than in `hint`: a hint renders between the
        label and the control, and the extra line reads as a warning about a field that
        is simply not required.
      */}
      <Field
        label="Phone (optional)"
        htmlFor={id('phone')}
        labelVariant={labelVariant}
        error={errors.phone?.message}
      >
        <PhoneInput
          id={id('phone')}
          country={country}
          onCountryChange={setCountry}
          invalid={Boolean(errors.phone)}
          describedBy={errors.phone ? `${id('phone')}-error` : undefined}
          inputProps={register('phone')}
        />
      </Field>

      <Field
        label="Enquiry"
        htmlFor={id('message')}
        labelVariant={labelVariant}
        required
        hint="The role, the seniority, and where it sits. Detail helps."
        error={errors.message?.message}
      >
        <Textarea
          id={id('message')}
          rows={4}
          placeholder="Tell us about your hiring needs…"
          aria-invalid={Boolean(errors.message)}
          aria-describedby={errors.message ? `${id('message')}-error` : `${id('message')}-hint`}
          {...register('message')}
        />
      </Field>

      {/*
        Honeypot.
        Positioned off-screen rather than `display: none`, because some bots skip
        hidden inputs. `tabIndex={-1}` and `aria-hidden` keep it away from keyboard and
        screen-reader users, and `autoComplete="off"` stops a password manager helpfully
        filling it in and flagging a real person as a bot.
      */}
      <div className="absolute left-[-9999px]" aria-hidden="true">
        <label htmlFor={id('website')}>Website</label>
        <input
          id={id('website')}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          {...register('website')}
        />
      </div>

      <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
        <Button
          type="submit"
          disabled={isSubmitting}
          className={cn('sm:self-start', submitClassName)}
        >
          {isSubmitting ? 'Sending…' : submitLabel}
        </Button>
        {secondaryAction}
      </div>

      <p className="text-xs text-muted">We use your details only to reply to this enquiry.</p>
    </form>
  );
}
